import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

/** Fraction of hearts to keep on mobile (0.5 = half) */
const MOBILE_HEARTS_RATIO = 0.5;

export type HeartsAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

type HeartParticle = {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  rot: number;
  life: number;
  maxLife: number;
  wob: number;
  wobAmp: number;
  dL: number;
  dR: number;
  dT: number;
};

/** Organic hearts — Delightful-er Forms / Background Animations spec (Canvas 2D). */
export const HeartsAnimation: FC<HeartsAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function tc(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
        case "light":
          return `hsla(${hue}, 48%, 52%, ${alpha * 0.65})`;
        case "darkdull":
          return `hsla(${hue}, 22%, 68%, ${alpha * 0.52})`;
        case "lightdull":
          return `hsla(${hue}, 12%, 52%, ${alpha * 0.5})`;
        default:
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    function mkHeart(): HeartParticle {
      return {
        x: Math.random() * canvasEl.width,
        y: Math.random() * canvasEl.height,
        size: Math.random() * 8 + 4,
        vx: (Math.random() - 0.5) * 0.22,
        vy: -(Math.random() * 0.06 + 0.02),
        rot: (Math.random() - 0.5) * 0.45,
        life: Math.random() * 320,
        maxLife: 320 + Math.random() * 240,
        wob: Math.random() * Math.PI * 2,
        wobAmp: Math.random() * 0.3 + 0.08,
        dL: (Math.random() - 0.5) * 0.28,
        dR: (Math.random() - 0.5) * 0.28,
        dT: (Math.random() - 0.5) * 0.18,
      };
    }

    function heartPath(cx: number, cy: number, s: number, dL: number, dR: number, dT: number): void {
      const tipY = cy + s * (0.9 + dT * 0.05);
      const notchY = cy - s * (0.65 - dT * 0.05);
      const eqY = cy - s * 0.28;
      const lobeTopY = cy - s * (1.0 + dT * 0.08);
      const rX = cx + s * (1.0 + dR * 0.1);
      const lX = cx - s * (1.0 + dL * 0.1);
      const rTopX = cx + s * (0.4 + dR * 0.06);
      const lTopX = cx - s * (0.4 + dL * 0.06);

      context.beginPath();
      context.moveTo(cx, tipY);
      context.bezierCurveTo(cx - s * (0.4 + dL * 0.05), cy + s * 0.5, lX, cy + s * 0.1, lX, eqY);
      context.bezierCurveTo(lX, lobeTopY, lTopX, lobeTopY, cx, notchY);
      context.bezierCurveTo(rTopX, lobeTopY, rX, lobeTopY, rX, eqY);
      context.bezierCurveTo(rX, cy + s * 0.1, cx + s * (0.4 + dR * 0.05), cy + s * 0.5, cx, tipY);
      context.closePath();
    }

    const heartCount = isMobile ? Math.round(22 * MOBILE_HEARTS_RATIO) : 22;
    const hearts: HeartParticle[] = Array.from({ length: heartCount }, () => mkHeart());

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, hearts);
    }

    window.addEventListener("resize", onResize);

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);

      for (const heart of hearts) {
        const progress = heart.life / heart.maxLife;

        let alpha = progress < 0.14 ? progress / 0.14 : progress > 0.8 ? (1 - progress) / 0.2 : 1;
        alpha *= 0.38;

        let scale: number;
        if (progress < 0.1) {
          scale = (progress / 0.1) * 1.18;
        } else if (progress < 0.18) {
          scale = 1.18 - 0.18 * ((progress - 0.1) / 0.08);
        } else if (progress < 0.8) {
          scale = 1.0 + Math.sin(heart.life * 0.028) * 0.025;
        } else {
          scale = (1 - progress) / 0.2;
        }
        scale = Math.max(0, scale);

        context.save();
        context.translate(heart.x, heart.y);
        context.rotate(heart.rot + Math.sin(heart.life * 0.018) * 0.04);
        context.scale(scale, scale);
        heartPath(0, 0, heart.size, heart.dL, heart.dR, heart.dT);
        context.fillStyle = tc(alpha);
        context.fill();
        context.restore();

        heart.life += frameSteps;
        heart.x += (heart.vx + Math.sin(heart.life * 0.022 + heart.wob) * heart.wobAmp) * frameSteps;
        heart.y += heart.vy * frameSteps;

        if (heart.life >= heart.maxLife) {
          Object.assign(heart, mkHeart(), { life: 0 });
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
